import { useCallback } from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { FF_WORKFLOW_APP_BUILDER_MODE } from '@/consts/featureFlags';
import { WorkflowEditorMode } from '@/enums/workflow-editor-mode.enum';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

interface WorkflowEditorModeStoreState {
  workflowEditorMode: WorkflowEditorMode;
  reset: () => void;
}

const workflowEditorModeStore = create<WorkflowEditorModeStoreState>()(
  devtools(
    (set) => ({
      workflowEditorMode: WorkflowEditorMode.EditWorkflow,
      reset: () => set({ workflowEditorMode: WorkflowEditorMode.EditWorkflow }),
    }),
    {
      name: 'Workflow Editor Mode',
      enabled: import.meta.env.DEV,
    },
  ),
);

/**
 * Workflow editor mode for UI. App builder mode is only available when the feature flag
 * resolves to enabled; until then, switching to app-builder is ignored.
 */
export function useWorkflowEditorMode() {
  const { value: isWorkflowAppBuilderEnabled } = useFeatureFlag(FF_WORKFLOW_APP_BUILDER_MODE);

  const workflowEditorMode = workflowEditorModeStore((state) => state.workflowEditorMode);
  const resetWorkflowEditorMode = workflowEditorModeStore((state) => state.reset);

  const setWorkflowEditorMode = useCallback(
    (mode: WorkflowEditorMode) => {
      if (mode === WorkflowEditorMode.AppBuilder && !isWorkflowAppBuilderEnabled) {
        return;
      }
      workflowEditorModeStore.setState({ workflowEditorMode: mode });
    },
    [isWorkflowAppBuilderEnabled],
  );

  return {
    workflowEditorMode,
    setWorkflowEditorMode,
    resetWorkflowEditorMode,
  };
}
